import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty, size } from 'lodash'
import { defaultDetailTableConfigs } from 'pages/Administration/components/Table/configs'
import { documentFieldColumn } from './config'
import DetailsFieldTable from 'pages/Administration/components/Table/DetailsFieldTable'
import {
  getRowNodeId,
  handleColumnDefs,
  sendNotification,
  sizeTableToFit,
  excelStyles,
  defaultExportParams,
  onExportFieldData
} from 'pages/Administration/helpers'
import {
  handleOpenModal,
  getFieldPermissionPayload
} from 'pages/Administration/Tabs/Instances/helpers'

import { frameworkComponents } from 'pages/Administration/constants'
import { createDocumentFieldsApi } from 'services/api/administration/documentFields'

import { openModal } from 'components/Modal/actions'

const DocumentFieldsPanel = React.memo(
  ({ admin, instanceType, documentTypeSelected }) => {
    const { documentFields } = admin

    const [gridApi, setGridApi] = useState(null)
    const [columnApi, setColumnApi] = useState(null)
    const [masterSelectedData, setMasterSelectedData] = useState([])
    const [detailSelectedData, setDetailSelectedData] = useState([])

    const [selectedRows, setSelectedRows] = useState([])

    const [updateSelected, setUpdateSelected] = useState({})
    const [cellHasBeenClicked, setCellHasBeenClicked] = useState(false)

    const { t, i18n } = useTranslation(['Administration', 'Common'])

    const { getDocumentFieldsByDT, updateDTFieldsPermissions } = useMemo(
      () => createDocumentFieldsApi(instanceType),
      [instanceType]
    )

    useEffect(async () => {
      setSelectedRows([])
      await getDocumentFieldsByDT(documentTypeSelected)
    }, [documentTypeSelected])

    useEffect(async () => {
      setSelectedRows(masterSelectedData.concat(detailSelectedData))
    }, [masterSelectedData, detailSelectedData])

    const onRowSelect = (rowList) => {
      setMasterSelectedData(rowList)
    }

    const onDetailRowSelect = (e) => {
      if (e.node.isSelected()) {
        setDetailSelectedData((currentList) => currentList.concat([e.data]))
      } else {
        setDetailSelectedData((currentList) =>
          currentList.filter((row) => row.id !== e.data.id)
        )
      }
    }

    const notifySuccess = (info) =>
      sendNotification({ t, info, kind: 'success' })

    const handleSelectedRow = () => {
      gridApi.deselectAll()
      gridApi.forEachDetailGridInfo((detailGridInfo) => {
        detailGridInfo.api.deselectAll()
      })
      gridApi.forEachNode((node) => {
        node.setExpanded(true)
      })
      setMasterSelectedData([])
      setDetailSelectedData([])
    }

    // RESET MODAL

    const onResetModalSubmit = async () => {
      const app = selectedRows
      const payload = []

      app.forEach((row) => {
        payload.push(getPayload(row, false, true))
      })

      try {
        await updateDTFieldsPermissions(payload)
        await getDocumentFieldsByDT(documentTypeSelected)
        handleSelectedRow()
        notifySuccess('reset')
      } catch (err) {
        console.error(err)
      }
    }

    const handleResetAllPermissions = () => {
      openModal('confirmAction', {
        modalHeading: t('Common:ResetInstance'),
        message: t('Common:InstanceResetConfirmationSelected'),
        onConfirm: onResetModalSubmit,
        primaryButtonText: t('Common:Reset'),
        secondaryButtonText: t('Common:Cancel')
      })
    }

    // UPDATE MODAL

    const updatePermissionModal = (isGroupMaster) => {
      openModal('confirmAction', {
        modalHeading: t('Common:UpdatePermissions'),
        message: isGroupMaster
          ? t('Common:PermissionConfirmation')
          : t(
              'Administration:RolesManagement.FieldModal.PermissionConfirmation'
            ),
        onConfirm: () => onUpdateModalSubmit(isGroupMaster),
        onCancel: () => onUpdateSecondaryModalSubmit(isGroupMaster),
        primaryButtonText: isGroupMaster
          ? t('Common:Overwrite')
          : t('Common:Confirm'),
        secondaryButtonText: isGroupMaster
          ? t('Common:UpdateOnlyOneRecord')
          : t('Common:Cancel')
      })
    }

    const onUpdateModalDefault = async (isBulk) => {
      const payload = getPayload(updateSelected, isBulk, false)

      try {
        await updateDTFieldsPermissions(payload)
        await getDocumentFieldsByDT(documentTypeSelected)
        handleSelectedRow()
        notifySuccess('Update')
      } catch (err) {
        console.error(err)
      }
    }
    const onUpdateModalSubmit = (isGroupMaster) => {
      onUpdateModalDefault(isGroupMaster)
    }

    const onUpdateSecondaryModalSubmit = (isGroupMaster) => {
      if (isGroupMaster) {
        onUpdateModalDefault(false)
      }
    }

    const getSinglePayload = (element, isReset, bulk, parent) => {
      const permissionPayload = isReset
        ? {
            read: false,
            import_: false,
            export: false,
            create: false,
            update: false,
            delete: false
          }
        : getFieldPermissionPayload(element, bulk, parent)

      const payload = {
        activityId: element.workingTableId,
        documentType: element.documentType,
        field: element.title,
        parent: element.parent || null,
        permissions: [
          { permission: permissionPayload, roleName: element.roleName }
        ]
      }
      return payload
    }

    function getPayload(element, bulk, isReset) {
      const singlePayload = getSinglePayload(element, isReset, bulk)
      if (!bulk) return singlePayload

      const bulkPayload = []
      bulkPayload.push(singlePayload)
      element.children?.forEach((child) => {
        bulkPayload.push(getSinglePayload(child, isReset, bulk, element))
      })
      return bulkPayload
    }

    const headerActions = useMemo(() => {
      if (!isEmpty(gridApi)) {
        return {
          selected: [
            {
              id: 'reset',
              title: t('Common:ResetPermissions'),
              handleClick: () => handleResetAllPermissions()
            }
          ],
          empty: [
            {
              id: 'exportRole',
              title: t('Administration:RolesManagement.Actions.ExportAll'),
              handleClick: () => {
                gridApi &&
                  columnApi &&
                  onExportFieldData(
                    gridApi,
                    columnApi,
                    t('RolesManagement.DocumentFieldTitle')
                  )
              }
            }
          ]
        }
      }
      return null
    }, [t, gridApi, handleResetAllPermissions, onExportFieldData])

    const isRowMaster = (dataItem) => dataItem?.children?.length > 0

    const onFirstDataRendered = (params) => {
      setTimeout(function () {
        params.api.forEachNode(function (node) {
          node.setExpanded(true)
        })
      }, 0)
    }

    const getDetailCellRendererParams = () => {
      return {
        detailGridOptions: {
          onCellClicked: (e) => {
            setUpdateSelected({
              roleName: e.data.roleName,
              column: e.colDef.colId,
              oldPermission: e.data.permission,
              documentType: e.data.documentType,
              title: e.data.title,
              workingTableId: e.data.workingTableId,
              parent: e.data.parent,
              children: null
            })
            // setIsGroupMaster(false)
            if (handleOpenModal(e)) {
              setCellHasBeenClicked(true)
            }
          },
          ...defaultDetailTableConfigs,
          animateRows: true,
          frameworkComponents: frameworkComponents,
          onRowSelected: onDetailRowSelect,
          columnDefs: handleColumnDefs(documentFieldColumn, t),
          onFirstDataRendered: (grid) => {
            sizeTableToFit(grid.api)
          }
        },
        getDetailRowData: ({ successCallback, data }) => {
          successCallback(data.children)
        }
      }
    }

    useEffect(() => {
      if (cellHasBeenClicked) {
        updatePermissionModal(!isEmpty(updateSelected.children))
        setCellHasBeenClicked(false)
      }
    }, [cellHasBeenClicked])

    const exportParams = useMemo(() => defaultExportParams(t), [t])

    return (
      <div>
        {/*<h2 className='no-action-view'>*/}
        {/*  {t('RolesManagement.DocumentFieldTitle')}*/}
        {/*</h2>*/}
        <DetailsFieldTable
          headerActions={headerActions}
          columnDefs={handleColumnDefs(documentFieldColumn, t)}
          rowData={size(documentFields) ? documentFields : []}
          getRowNodeId={getRowNodeId}
          masterDetail
          isRowMaster={isRowMaster}
          key={i18n.language}
          onFirstDataRendered={onFirstDataRendered}
          detailCellRendererParams={getDetailCellRendererParams}
          onCellClicked={(e) => {
            const enhancedChildren = e.data.children?.map((child) => {
              return {
                roleName: child.roleName,
                column: e.colDef.colId,
                oldPermission: e.data.permission,
                documentType: child.documentType,
                title: child.title,
                workingTableId: child.workingTableId,
                parent: child.parent
              }
            })
            setUpdateSelected({
              roleName: e.data.roleName,
              column: e.colDef.colId,
              oldPermission: e.data.permission,
              documentType: e.data.documentType,
              title: e.data.title,
              workingTableId: e.data.workingTableId,
              parent: null,
              children: enhancedChildren
            })

            if (handleOpenModal(e)) {
              setCellHasBeenClicked(true)
            }
          }}
          onGridReady={({ api, columnApi }) => {
            setGridApi(api)
            setColumnApi(columnApi)
            sizeTableToFit(api)
          }}
          onSelectionChanged={onRowSelect}
          selectedRows={selectedRows}
          frameworkComponents={frameworkComponents}
          excelStyles={excelStyles}
          defaultExportParams={exportParams}
        />
      </div>
    )
  }
)

export default DocumentFieldsPanel
